"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { setAuth } from "../Slices/AuthSlice";
import { addData } from "../Slices/DataSlice";
import { resetChatState } from "../Slices/ChatSlice";
import {
  buildUrl,
  pathForScreen,
  screenForPath,
  isBmrSharedView,
} from "../common/navigation";
import {
  HEADER_SPECIAL_USERS,
  SETTINGS_ROLES,
  isSpecialUser,
  isCOO,
  isPresident,
} from "../common/privileges";

import { MdPermContactCalendar } from "react-icons/md";
import { FaBell, FaFileAlt, FaListAlt, FaTimes, FaWifi } from "react-icons/fa";
import {
  FaHouse,
  FaTicket,
  FaListCheck,
  FaUser,
  FaRightFromBracket,
  FaBars,
  FaChevronDown,
  FaChevronRight,
  FaGear,
  FaChartLine,
  FaHospital,
  FaComputer,
  FaPeopleGroup,
  FaLayerGroup,
} from "react-icons/fa6";
import CustomModal from "../common/CustomModal";
import UserProfileBody from "./UserProfileBody";
import useListenMessages from "../hooks/useListenMessages";
import useMeetingReminder from "../hooks/useMeetingReminder";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { getTheme, setTheme } from "../../theme";
import ThemePicker from "../common/ThemePicker";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Index = ({ k, setIsDrawerOpen, setIsNotificationsOpen }) => {
  const { data } = useSelector((state) => state?.data?.userInfo?.data);
  const speacialUser = isSpecialUser(data?.EmpId, HEADER_SPECIAL_USERS);
  const isPresidentUser = isPresident(data?.EmpId);
  const isCooUser = isCOO(data?.EmpId);
  const showSettingsButton =
    (SETTINGS_ROLES.includes(data?.RoleId) || speacialUser) &&
    !isPresidentUser && !isCooUser;

  const showProSoftwareViews = data?.RoleId === 14;
  const profileImage = localStorage.getItem("profilePicture");
  const fullName = localStorage.getItem("fullName");
  const location = useLocation();
  const routerNavigate = useNavigate();
  const activeTab = new URLSearchParams(location.search).get("tab");
  // Service ID: #3867 — Biller report navigation and active-route state.
  const bmrBillerSummaryPath = pathForScreen("BillerSummary");
  const isBillerSummaryRoute =
    location.pathname === bmrBillerSummaryPath ||
    location.pathname.startsWith(`${bmrBillerSummaryPath}/`);
  const activeButton = isBmrSharedView(location)
    ? "BMRSoftwareTickets"
    : screenForPath(location.pathname);
  const isBillerSummaryActive =
    activeButton === "BillerSummary" ||
    isBillerSummaryRoute ||
    activeTab === "Biller Summary";
  // Support Tickes section is commented out
  // useMeetingReminder();
  useListenMessages();
  const notificationCount = useSelector(
    (state) => state.notifications?.unreadCount || 0
  );
  const connection = useSelector((state) => state.socket.socketConnection);
  const dropdownRef = useRef(null);
  const checklistRef = useRef(null);
  const clientRef = useRef(null);
  const softwareRef = useRef(null);
  const supportRef = useRef(null);
  const clientHoverTimeoutRef = useRef(null);
  const softwareHoverTimeoutRef = useRef(null);
  const supportHoverTimeoutRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const dispatch = useDispatch();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [checklistDropdownOpen, setChecklistDropdownOpen] = useState(false);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [softwareDropdownOpen, setSoftwareDropdownOpen] = useState(false);
  const [supportDropdownOpen, setSupportDropdownOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [theme, updateTheme] = useState(getTheme);
  const changeTheme = (next) => {
    setTheme(next);
    updateTheme(next);
  };
  useEffect(() => {
    const syncTheme = (event) => updateTheme(event.detail);
    window.addEventListener("app-theme-change", syncTheme);
    return () => window.removeEventListener("app-theme-change", syncTheme);
  }, []);
  const profileMenuRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("connected"); // Start with connected to avoid flicker
  const [showReconnectAlert, setShowReconnectAlert] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);

  // Debounce connection status to prevent flickering
  const debouncedConnectionStatus = useDebounce(connectionStatus, 1000);
  // Used by the profile pill's dropdown to surface a "Reconnect" action —
  // previously only shown inside the (now-removed) desktop ProfileWithIndicator.
  const shouldShowReconnect =
    showReconnectAlert &&
    debouncedConnectionStatus === "disconnected" &&
    isTabVisible;

  const clearHoverTimeout = (timeoutRef) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleClose = (timeoutRef, closeFn, delay = 150) => {
    clearHoverTimeout(timeoutRef);
    timeoutRef.current = setTimeout(closeFn, delay);
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Log out?",
      text: "You'll need to sign in again to continue.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#46A8DF",
      cancelButtonColor: "#383F43",
      confirmButtonText: "Yes, log out",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) return;

      // Clear all active toasts
      toast.remove();

      connection?.stop();
      dispatch(setAuth(false));
      dispatch(addData({ name: "practice", data: [] }));
      dispatch(addData({ name: "userInfo", data: [] }));
      // Chat's conversation/group lists aren't part of the persisted
      // DataSlice — they live in the in-memory ChatSlice ("conversation")
      // for the lifetime of the tab, so they must be cleared explicitly or
      // they'd still be visible if a different user logs in on this same
      // device right after (see resetChatState in ChatSlice.js).
      dispatch(resetChatState());

      // Clear per-user display data so it doesn't leak to the next user who
      // logs in on this device (these are set in Login.jsx / UserProfileBody.jsx
      // and read right here in Header/Index.js).
      localStorage.removeItem("profilePicture");
      localStorage.removeItem("fullName");

      routerNavigate(pathForScreen("Login"));
    });
  };

  // Reconnect function
  const handleReconnect = async () => {
    if (!connection) {
      toast.error("No connection available");
      return;
    }

    setConnectionStatus("reconnecting");
    toast.loading("Reconnecting...", { id: "reconnect" });

    try {
      // Only stop if connected
      if (connection.state === "Connected" || connection.state === "connected") {
        await connection.stop();
      }

      // Start new connection
      await connection.start();

      setConnectionStatus("connected");
      setShowReconnectAlert(false);
      toast.success("Reconnected successfully!", { id: "reconnect" });
    } catch (err) {
      console.error("Reconnection failed:", err);
      setConnectionStatus("disconnected");
      toast.error("Failed to reconnect. Please try again.", {
        id: "reconnect",
      });
    }
  };

  // Navigation handlers
  const navigate = (screenOrPath) => {
    const target =
      typeof screenOrPath === "string" &&
      (screenOrPath.startsWith("/") || screenOrPath.startsWith("http"))
        ? screenOrPath
        : pathForScreen(screenOrPath);

    routerNavigate(target);
    setMobileMenuOpen(false);
  };

  const setting = () => navigate("Main");
  const dashboard = () => navigate("Dashboard");
  const ticketsHandler = () => navigate("SearchedTicket");
  const crfHandler = () => navigate("ChangeRequestForm");

  const checklistAnalyticsHandler = () => {
    navigate("ChecklistAnalytics");
    setChecklistDropdownOpen(false);
  };
  const checklistCrudHandler = () => {
    navigate("Checklist");
    setChecklistDropdownOpen(false);
  };
  const attendanceHandler = () => navigate("Attendance");
  const toggleProfileModal = () => setIsProfileModalOpen(!isProfileModalOpen);

  // Monitor tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === "visible";
      setIsTabVisible(visible);

      // When tab becomes visible, check connection but don't change status immediately
      if (visible && connection) {
        setTimeout(() => {
          if (connection.state === "Connected" || connection.state === "connected") {
            setConnectionStatus("connected");
            setShowReconnectAlert(false);
          }
        }, 1000); // Wait 1 second before checking
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Set initial visibility
    setIsTabVisible(document.visibilityState === "visible");

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [connection]);

  // Monitor connection status with better handling
  useEffect(() => {
    if (!connection) {
      // If no connection, set disconnected after delay
      const timeout = setTimeout(() => {
        setConnectionStatus("disconnected");
        setShowReconnectAlert(true);
      }, 2000);
      return () => clearTimeout(timeout);
    }

    let isMounted = true;
    let checkTimeout;

    const getConnectionState = () => {
      if (connection.state !== undefined) {
        return connection.state;
      } else if (connection.connectionState !== undefined) {
        return connection.connectionState;
      }
      return "Disconnected";
    };

    const updateStatus = (state) => {
      if (!isMounted || !isTabVisible) return;

      // Only update if state has actually changed
      setConnectionStatus(prev => {
        if (prev !== state) {
          return state;
        }
        return prev;
      });

      // Only show reconnect alert if disconnected for real
      if (state === "disconnected" || state === "Disconnected") {
        // Delay showing reconnect alert to prevent flicker
        checkTimeout = setTimeout(() => {
          if (isMounted && isTabVisible) {
            setShowReconnectAlert(true);
          }
        }, 2000);
      } else {
        setShowReconnectAlert(false);
      }
    };

    // Initial check with delay
    checkTimeout = setTimeout(() => {
      if (!isMounted) return;

      const state = getConnectionState();
      if (state === "Connected" || state === "connected") {
        updateStatus("connected");
      } else if (state === "Connecting" || state === "Reconnecting") {
        updateStatus("reconnecting");
      } else {
        updateStatus("disconnected");
      }
    }, 500);

    // Event handlers
    const handleConnectionClose = () => {
      if (isMounted && isTabVisible) {
        updateStatus("disconnected");
      }
    };

    const handleReconnecting = () => {
      if (isMounted && isTabVisible) {
        updateStatus("reconnecting");
      }
    };

    const handleReconnected = () => {
      if (isMounted && isTabVisible) {
        updateStatus("connected");
      }
    };

    // Set up event listeners
    if (connection.onclose) connection.onclose(handleConnectionClose);
    if (connection.onreconnecting) connection.onreconnecting(handleReconnecting);
    if (connection.onreconnected) connection.onreconnected(handleReconnected);

    // For newer SignalR versions
    if (connection.on) {
      connection.on("close", handleConnectionClose);
      connection.on("reconnecting", handleReconnecting);
      connection.on("reconnected", handleReconnected);
    }

    // Cleanup
    return () => {
      isMounted = false;
      clearTimeout(checkTimeout);

      // Remove event listeners
      if (connection.off) {
        connection.off("close", handleConnectionClose);
        connection.off("reconnecting", handleReconnecting);
        connection.off("reconnected", handleReconnected);
      }
    };
  }, [connection, isTabVisible]);

  // Fetch and update online users status
  useEffect(() => {
    if (connection && debouncedConnectionStatus === "connected") {
      connection.on("UserStatusChanged", (userId, status) => {
        // Handle user status changes
      });

      connection
        .invoke("GetOnlineUsers")
        .then((users) => {
          const updatedOnlineUsers = users.reduce((acc, user) => {
            acc[user] = "true";
            return acc;
          }, {});
        })
        .catch((err) => {
          console.error("Error fetching online users:", err);
        });

      return () => {
        if (connection.off) {
          connection.off("UserStatusChanged");
        }
      };
    }
  }, [connection, data?.UserId, debouncedConnectionStatus]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    let isDesktop = window.innerWidth >= 1024; // lg breakpoint

    const handleResize = () => {
      isDesktop = window.innerWidth >= 1024;
    };

    const handleClickOutside = (event) => {
      if (isDesktop) {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setDropdownOpen(false);
        }
        if (
          checklistRef.current &&
          !checklistRef.current.contains(event.target)
        ) {
          setChecklistDropdownOpen(false);
        }
        if (clientRef.current && !clientRef.current.contains(event.target)) {
          setClientDropdownOpen(false);
        }
        if (
          softwareRef.current &&
          !softwareRef.current.contains(event.target)
        ) {
          setSoftwareDropdownOpen(false);
        }
        if (supportRef.current && !supportRef.current.contains(event.target)) {
          setSupportDropdownOpen(false);
        }
      }

      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close the profile pill's dropdown on outside click or Escape.
  useEffect(() => {
    if (!profileMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setProfileMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setProfileMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [profileMenuOpen]);

  // Profile Image with Online Indicator Component
  const ProfileWithIndicator = ({
    size = "medium",
    showText = true,
    mobile = false,
  }) => {
    // Use debounced status for display, but don't show reconnecting during tab switch
    const displayStatus = debouncedConnectionStatus;
    const shouldShowReconnect = showReconnectAlert && displayStatus === "disconnected" && isTabVisible;

    return (
      <div
        className={`flex items-center ${mobile ? "space-x-3" : "py-2 px-4 xl:px-6"
          }`}
      >
        <div className="relative">
          <img
            className={`${size === "small"
              ? "w-8 h-8"
              : size === "medium"
                ? "w-10 h-10 md:w-12 md:h-12"
                : "w-16 h-16"
              } rounded-full object-cover border-2 ${displayStatus === "connected"
                ? "border-green-200"
                : "border-red-200"
              } cursor-pointer hover:${displayStatus === "connected"
                ? "border-green-400"
                : "border-red-400"
              } transition-all duration-200 shadow-sm hover:shadow-md`}
            src={
              profileImage !== "null"
                ? `data:image/png;base64,${profileImage}`
                : "https://cdn-icons-png.flaticon.com/512/149/149071.png"
            }
            alt="profile"
            onClick={toggleProfileModal}
            title="Open Profile"
          />
          {/* Online/Offline Indicator */}
          <div
            className={`absolute bottom-0 right-0 ${mobile ? "w-2.5 h-2.5" : "w-3 h-3"
              } rounded-full border-2 border-white ${displayStatus === "connected"
                ? "bg-green-500"
                : displayStatus === "reconnecting"
                  ? "bg-yellow-500 animate-pulse"
                  : "bg-red-500"
              }`}
            title={
              displayStatus === "connected"
                ? "Online"
                : displayStatus === "reconnecting"
                  ? "Reconnecting..."
                  : "Offline"
            }
          />
        </div>

        {/* Text info */}
        {showText && (
          <div
            className={`${mobile ? "min-w-0 flex-1" : "hidden lg:flex flex-col ml-3 min-w-0"
              }`}
          >
            <div className="flex items-center space-x-2">
              <span className="text-base font-medium text-gray-800 truncate">
                <span className="font-bold">Welcome </span>
                <span className="font-bold text-sideNavbg text-lg md:text-xl">
                  {fullName}
                </span>
              </span>
            </div>
            <span className="text-sm md:text-base truncate">{data?.Email}</span>
            <div className="flex items-center space-x-2 mt-1 relative">
              {shouldShowReconnect && (
                <button
                  onClick={handleReconnect}
                  className="text-sm font-medium bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-1"
                >
                  <FaWifi className="text-xs" />
                  <span>Reconnect</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="pc-app-header flex w-full h-full bg-gradient-to-r from-[#a0a7af] to-[#ced4b4] border-b-[1.5px] py-1 border-[#f36d00] relative">
      {!k && (
        <>
          {/* "President" sees this portal label instead of the
              regular Client/Software/Support navigation, which is hidden
              for this user below. `fixed` + `inset-x-0` centers it against
              the full page width (the overall header, Logo section
              included) rather than just this Nav section's own width —
              this component only owns the Nav section, which starts after
              the Logo, so centering relative to its own local container
              would be off-center against the whole header. The header
              itself never scrolls (the page's scrollable area lives below
              it), so `fixed` looks identical to `absolute` here. */}
          {isPresidentUser && (
            <div className="hidden xl:flex items-center justify-center fixed inset-x-0 top-0 h-16 sm:h-18 pointer-events-none">
              <span className="text-[32px] font-bold text-gray-800 whitespace-nowrap">
                {"BellMedex Reporting Portal (BRP)"}
              </span>
            </div>
          )}

          {/* Mobile Menu Button - Shown on mobile */}
          <div
            className="flex lg:hidden items-center ml-auto px-4"
            ref={mobileMenuRef}
          >
            <button
              type="button"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-all duration-200 hover:shadow-md"
            >
              {mobileMenuOpen ? (
                <FaTimes className="text-lg text-gray-600" />
              ) : (
                <FaBars className="text-lg text-gray-600" />
              )}
            </button>

            {/* Mobile Dropdown Menu */}
            {mobileMenuOpen && (
              <div className="absolute right-4 top-full mt-2 w-64 max-h-[calc(100dvh-90px)] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                <div className="py-2">
                  {/* Mobile User Info */}
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <ProfileWithIndicator
                      size="small"
                      showText={true}
                      mobile={true}
                    />
                  </div>

                  {/* Rest of mobile navigation items */}
                  {isPresidentUser || showProSoftwareViews || isCooUser ? (
                    <>
                      {/* BPR entry — COO users are limited to Home (Biller Summary) and To-do only. */}
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <MobileNavItem
                          label="Home"
                          handler={() => navigate(isCooUser ? buildUrl("BillerSummary", { tab: "Biller Summary" }) : "BMRSoftwareTickets")}
                          icon={<FaHouse className="text-blue-500" />}
                          active={
                            isCooUser
                              ? isBillerSummaryActive
                              : ["BMRSearchedTicket", "BMRSoftwareTickets", "ManagementBoard"].includes(activeButton) ||
                                isBillerSummaryActive
                          }
                        />
                      </div>

                      {!isPresidentUser && !isCooUser && (
                        <>
                          {/* Client Dropdown */}
                          <div className="border-t border-gray-100">
                            <button
                              className="flex items-center justify-between w-full px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                              onClick={() =>
                                setClientDropdownOpen(!clientDropdownOpen)
                              }
                            >
                              <div className="flex items-center">
                                <FaUser className="mr-3 text-blue-500" />
                                <span>Client</span>
                              </div>
                              <FaChevronDown
                                className={`text-xs transition-transform duration-200 ${clientDropdownOpen ? "transform rotate-180" : ""
                                  }`}
                              />
                            </button>
                            {clientDropdownOpen && (
                              <div className="pl-4">
                                <MobileNavItem
                                  label="Dashboard"
                                  handler={dashboard}
                                  icon={<FaHouse className="text-blue-500" />}
                                  active={activeButton === "Dashboard"}
                                />
                                <MobileNavItem
                                  label="Tickets"
                                  handler={ticketsHandler}
                                  icon={<FaTicket className="text-green-500" />}
                                  active={activeButton === "SearchedTicket"}
                                />
                                <MobileNavItem
                                  label="CRF"
                                  handler={crfHandler}
                                  icon={<FaFileAlt className="text-purple-500" />}
                                  active={activeButton === "ChangeRequestForm"}
                                />
                              </div>
                            )}
                          </div>

                          {/* Software Dropdown */}
                          <div className="border-t border-gray-100">
                            <button
                              className="flex items-center justify-between w-full px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                              onClick={() =>
                                setSoftwareDropdownOpen(!softwareDropdownOpen)
                              }
                            >
                              <div className="flex items-center">
                                <FaComputer className="mr-3 text-purple-500" />
                                <span>Software</span>
                              </div>
                              <FaChevronDown
                                className={`text-xs transition-transform duration-200 ${softwareDropdownOpen ? "transform rotate-180" : ""
                                  }`}
                              />
                            </button>
                            {softwareDropdownOpen && (
                              <div className="pl-4">
                                <MobileNavItem
                                  label="Analytics"
                                  handler={() => navigate("SoftwareAnalytics")}
                                  icon={<FaHouse className="text-blue-500" />}
                                  active={activeButton === "SoftwareAnalytics"}
                                />
                                <MobileNavItem
                                  label="ProSoftware Engineer Tickets"
                                  handler={() => navigate("SoftwareTickets")}
                                  icon={<FaTicket className="text-green-500" />}
                                  active={activeButton === "SoftwareTickets"}
                                />{" "}
                                <MobileNavItem
                                  label="EHR/PMS Oversight"
                                  handler={() => navigate("Checklist")}
                                  icon={<FaListCheck className="text-indigo-500" />}
                                  active={activeButton === "Checklist"}
                                />
                                <MobileNavItem
                                  label="Project Status"
                                  handler={() => navigate("ProjectStatus")}
                                  icon={<FaListAlt className="text-teal-500" />}
                                  active={activeButton === "ProjectStatus"}
                                />
                                <MobileNavItem
                                  label="ProSoftware Team"
                                  handler={() => navigate("ProSoftwareTeam")}
                                  icon={
                                    <FaPeopleGroup className="text-orange-500" />
                                  }
                                  active={activeButton === "ProSoftwareTeam"}
                                />
                              </div>
                            )}
                          </div>

                          {/* Support Productivity Dropdown */}
                          {/* <div className="border-t border-gray-100">
                            <button
                              className="flex items-center justify-between w-full px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                              onClick={() =>
                                setSupportDropdownOpen(!supportDropdownOpen)
                              }
                            >
                              <div className="flex items-center">
                                <FaUser className="mr-3 text-red-500" />
                                <span>Support</span>
                              </div>
                              <FaChevronDown
                                className={`text-xs transition-transform duration-200 ${supportDropdownOpen ? "transform rotate-180" : ""
                                  }`}
                              />
                            </button>
                            {supportDropdownOpen && (
                              <div className="pl-4">
                                <MobileNavItem
                                  label="Analytics"
                                  handler={() => navigate("SupportAnalytics")}
                                  icon={<FaHouse className="text-blue-500" />}
                                  active={activeButton === "SupportAnalytics"}
                                />
                                <MobileNavItem
                                  label="Support Tickets"
                                  handler={() => navigate("SupportTickets")}
                                  icon={<FaTicket className="text-green-500" />}
                                  active={activeButton === "SupportTickets"}
                                />
                              </div>
                            )}
                          </div> */}
                        </>
                      )}
                    </>
                  ) : (
                    // Regular navigation for non-admin roles
                    <>
                      <MobileNavItem
                        label="Dashboard"
                        handler={dashboard}
                        icon={<FaHouse className="text-blue-500" />}
                        active={activeButton === "Dashboard"}
                      />
                      <MobileNavItem
                        label="Tickets"
                        handler={ticketsHandler}
                        icon={<FaTicket className="text-green-500" />}
                        active={activeButton === "SearchedTicket"}
                      />
                      <MobileNavItem
                        label="CRF"
                        handler={crfHandler}
                        icon={<FaFileAlt className="text-purple-500" />}
                        active={activeButton === "ChangeRequestForm"}
                      />
                    </>
                  )}

                  {/* Common items; COO users are limited to To-do only. */}
                  {[
                    ...(!isCooUser ? [
                      {
                        label: "Attendance",
                        handler: attendanceHandler,
                        icon: (
                          <MdPermContactCalendar className="text-orange-500" />
                        ),
                        active: activeButton === "Attendance",
                      },
                    ] : []),
                    {
                      label: "To-do",
                      handler: () => {
                        setIsDrawerOpen((prev) => !prev);
                        setMobileMenuOpen(false);
                      },
                      icon: <FaListAlt className="text-teal-500" />,
                      active: false,
                    },
                    ...(!isCooUser && showSettingsButton
                      ? [
                        {
                          label: "Settings",
                          handler: setting,
                          icon: <FaGear className="text-gray-500" />,
                          active: activeButton === "Main",
                        },
                      ]
                      : []),
                    ...(!isCooUser ? [{
                      label: `Notifications${notificationCount > 0 ? ` (${notificationCount})` : ""
                        }`,
                      handler: () => {
                        setIsNotificationsOpen((prev) => !prev);
                        setMobileMenuOpen(false);
                      },
                      icon: <FaBell className="text-green-500" />,
                      active: false,
                    }] : []),
                  ].map((item) => (
                    <MobileNavItem key={item.label} {...item} />
                  ))}

                  <ThemePicker theme={theme} onChange={changeTheme} />

                  {/* Mobile Logout */}
                  <div className="border-t border-gray-100 mt-2 pt-2">
                    <button
                      className="flex items-center w-full px-4 py-3 text-[15px] font-medium text-red-600 hover:bg-red-50 transition-colors duration-200"
                      onClick={handleLogout}
                    >
                      <FaRightFromBracket className="mr-3" />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Navigation - Aligned to right */}
          <div className="hidden lg:flex items-center justify-end flex-1 px-4">
            <div className="flex items-center space-x-2">
              {isPresidentUser || showProSoftwareViews || isCooUser ? (
                <>
                  {/* BPR entry for Admin. COO users are limited to Home (Biller Summary) plus To-do. */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => navigate(isCooUser ? buildUrl("BillerSummary", { tab: "Biller Summary" }) : "BMRSoftwareTickets")}
                      className={`pc-nav-button flex items-center px-4 py-2 text-base font-semibold rounded-lg transition-all duration-200 ${
                        isCooUser
                          ? isBillerSummaryActive
                          : ["BMRSearchedTicket", "BMRSoftwareTickets", "ManagementBoard"].includes(activeButton) ||
                            isBillerSummaryActive
                      ? "bg-white shadow-md text-blue-700 border border-blue-200"
                      : "bg-white/70 hover:bg-white hover:shadow-md text-gray-700 border border-gray-200"}`}
                    >
                      <FaHouse className="mr-2 text-blue-500" />
                      <span>Home</span>
                    </button>
                  </div>

                  {!isPresidentUser && !isCooUser && (
                    <>
                      {/* Client Dropdown for Admin */}
                      <div
                        className="relative"
                        ref={clientRef}
                        onMouseEnter={() => {
                          clearHoverTimeout(clientHoverTimeoutRef);
                          setClientDropdownOpen(true);
                          setSoftwareDropdownOpen(false);
                          setSupportDropdownOpen(false);
                        }}
                        onMouseLeave={() =>
                          scheduleClose(
                            clientHoverTimeoutRef,
                            () => setClientDropdownOpen(false)
                          )
                        }
                      >
                        <button
                          type="button"
                          className={`pc-nav-button flex items-center px-4 py-2 text-base font-semibold rounded-lg transition-all duration-200 ${[
                            "Dashboard",
                            "SearchedTicket",
                            "ChangeRequestForm",
                          ].includes(activeButton)
                            ? "bg-white shadow-md text-blue-700 border border-blue-200"
                            : "bg-white/70 hover:bg-white hover:shadow-md text-gray-700 border border-gray-200"
                            }`}
                        >
                          <FaHospital className="pc-client-icon mr-2 text-green-500" />
                          <span>Client</span>
                          <FaChevronDown
                            className={`ml-2 text-base transition-transform duration-200 ${clientDropdownOpen ? "transform rotate-180" : ""
                              }`}
                          />
                        </button>
                        {clientDropdownOpen && (
                          <div
                            className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden"
                            onMouseEnter={() =>
                              clearHoverTimeout(clientHoverTimeoutRef)
                            }
                            onMouseLeave={() =>
                              scheduleClose(
                                clientHoverTimeoutRef,
                                () => setClientDropdownOpen(false)
                              )
                            }
                          >
                            <DropdownItem
                              active={activeButton === "Dashboard"}
                              label="Dashboard"
                              icon={<FaHouse className="text-blue-500" />}
                              onClick={dashboard}
                            />
                            <DropdownItem
                              active={activeButton === "SearchedTicket"}
                              label="Tickets"
                              icon={<FaTicket className="text-green-500" />}
                              onClick={ticketsHandler}
                            />
                            <DropdownItem
                              active={activeButton === "ChangeRequestForm"}
                              label="CRF"
                              icon={<FaFileAlt className="text-purple-500" />}
                              onClick={crfHandler}
                            />
                          </div>
                        )}
                      </div>

                      {/* Software Dropdown for Admin */}
                      <div
                        className="relative"
                        ref={softwareRef}
                        onMouseEnter={() => {
                          clearHoverTimeout(softwareHoverTimeoutRef);
                          setSoftwareDropdownOpen(true);
                          setClientDropdownOpen(false);
                          setSupportDropdownOpen(false);
                        }}
                        onMouseLeave={() =>
                          scheduleClose(
                            softwareHoverTimeoutRef,
                            () => setSoftwareDropdownOpen(false)
                          )
                        }
                      >
                        <button
                          type="button"
                          className={`pc-nav-button flex items-center px-4 py-2 text-base font-semibold rounded-lg transition-all duration-200 ${[
                            "SoftwareAnalytics",
                            "SoftwareTickets",
                            "Checklist",
                            "ProjectStatus",
                            "ProSoftwareTeam",
                          ].includes(activeButton)
                            ? "bg-white shadow-md text-blue-700 border border-blue-200"
                            : "bg-white/70 hover:bg-white hover:shadow-md text-gray-700 border border-gray-200"
                            }`}
                        >
                          <FaComputer className="mr-2 text-purple-500" />
                          <span>Software</span>
                          <FaChevronDown
                            className={`ml-2 text-base transition-transform duration-200 ${softwareDropdownOpen ? "transform rotate-180" : ""
                              }`}
                          />
                        </button>
                        {softwareDropdownOpen && (
                          <div
                            className="absolute right-0 mt-2 w-[300px] bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden"
                            onMouseEnter={() =>
                              clearHoverTimeout(softwareHoverTimeoutRef)
                            }
                            onMouseLeave={() =>
                              scheduleClose(
                                softwareHoverTimeoutRef,
                                () => setSoftwareDropdownOpen(false)
                              )
                            }
                          >
                            <DropdownItem
                              active={activeButton === "SoftwareAnalytics"}
                              label="Analytics"
                              icon={<FaChartLine className="text-purple-500" />}
                              onClick={() => navigate("SoftwareAnalytics")}
                            />
                            <DropdownItem
                              active={activeButton === "SoftwareTickets"}
                              label="ProSoftware Service Tickets"
                              icon={<FaTicket className="text-green-500" />}
                              onClick={() => navigate("SoftwareTickets")}
                            />
                            <DropdownItem
                              active={activeButton === "Checklist"}
                              label="EHR/PMS Oversight"
                              icon={<FaListCheck className="text-indigo-500" />}
                              onClick={() => navigate("Checklist")}
                            />

                            <>
                              {" "}
                              <DropdownItem
                                active={activeButton === "ProjectStatus"}
                                label="Project Status"
                                icon={<FaListAlt className="text-orange-500" />}
                                onClick={() => navigate("ProjectStatus")}
                              />
                              <DropdownItem
                                active={activeButton === "ProSoftwareTeam"}
                                label="ProSoftware Team"
                                icon={<FaPeopleGroup className="text-blue-500" />}
                                onClick={() => navigate("ProSoftwareTeam")}
                              />{" "}
                            </>
                          </div>
                        )}
                      </div>

                      {/* Support Productivity Dropdown for Admin */}
                      {/* <div
                    className="relative"
                    ref={supportRef}
                    onMouseEnter={() => {
                      clearHoverTimeout(supportHoverTimeoutRef);
                      setSupportDropdownOpen(true);
                      setClientDropdownOpen(false);
                      setSoftwareDropdownOpen(false);
                    }}
                    onMouseLeave={() =>
                      scheduleClose(
                        supportHoverTimeoutRef,
                        () => setSupportDropdownOpen(false)
                      )
                    }
                  >
                    <button
                      type="button"
                      className={`pc-nav-button flex items-center px-4 py-2 text-base font-semibold rounded-lg transition-all duration-200 ${["SupportAnalytics", "SupportTickets"].includes(
                        activeButton
                      )
                          ? "bg-white shadow-md text-blue-700 border border-blue-200"
                          : "bg-white/70 hover:bg-white hover:shadow-md text-gray-700 border border-gray-200"
                        }`}
                    >
                      <FaUser className="mr-2 text-red-500" />
                      <span>Support</span>
                      <FaChevronDown
                        className={`ml-2 text-base transition-transform duration-200 ${supportDropdownOpen ? "transform rotate-180" : ""
                          }`}
                      />
                    </button>
                    {supportDropdownOpen && (
                      <div
                        className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden"
                        onMouseEnter={() =>
                          clearHoverTimeout(supportHoverTimeoutRef)
                        }
                        onMouseLeave={() =>
                          scheduleClose(
                            supportHoverTimeoutRef,
                            () => setSupportDropdownOpen(false)
                          )
                        }
                      >
                        <DropdownItem
                          active={activeButton === "SupportAnalytics"}
                          label="Analytics"
                          icon={<FaChartLine className="text-purple-500" />}
                          onClick={() => navigate("SupportAnalytics")}
                        />
                        <DropdownItem
                          active={activeButton === "SupportTickets"}
                          label="Support Tickets"
                          icon={<FaTicket className="text-green-500" />}
                          onClick={() => navigate("SupportTickets")}
                        />
                      </div>
                    )}
                  </div> */}
                    </>
                  )}
                </>
              ) : (
                // Regular navigation for non-admin roles
                <>
                  {/* Dashboard Button */}
                  <NavButton
                    active={activeButton === "Dashboard"}
                    label="Dashboard"
                    icon={<FaHouse className="text-blue-500" />}
                    onClick={dashboard}
                  />

                  {/* Tickets Button */}
                  <NavButton
                    active={activeButton === "SearchedTicket"}
                    label="Tickets"
                    icon={<FaTicket className="text-green-500" />}
                    onClick={ticketsHandler}
                  />
                  <NavButton
                    active={activeButton === "ChangeRequestForm"}
                    label="CRF"
                    icon={<FaFileAlt className="text-purple-500" />}
                    onClick={crfHandler}
                  />
                </>
              )}

              {!isCooUser && (
                <NavButton
                  active={activeButton === "Attendance"}
                  label="Attendance"
                  icon={<MdPermContactCalendar className="text-orange-500" />}
                  onClick={attendanceHandler}
                />
              )}
              {/* To-do Button */}
              <button
                type="button"
                className="pc-nav-button flex items-center px-4 py-2 text-base font-semibold rounded-lg bg-white/70 hover:bg-white hover:shadow-md text-gray-700 border border-gray-200 transition-all duration-200 whitespace-nowrap"
                onClick={() => setIsDrawerOpen((prev) => !prev)}
              >
                <FaListAlt className="mr-2 text-teal-500" />
                <span>To-do</span>
              </button>

              {!isCooUser && (
                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center justify-center h-[39px] px-3 rounded-lg bg-white/70 hover:bg-white hover:shadow-md text-gray-700 border border-gray-200 transition-all duration-200 relative"
                    onClick={() => setIsNotificationsOpen((prev) => !prev)}
                  >
                    <FaBell className="text-green-500 text-lg" />
                    {notificationCount > 0 && (
                      <span
                        className={`absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-[20px] px-1 flex items-center justify-center shadow-md transition-transform duration-300 ease-in-out ${notificationCount > 99
                          ? "text-[9px] min-w-[22px] h-[22px]"
                          : ""
                          }`}
                      >
                        {notificationCount > 99 ? "99+" : notificationCount}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Profile pill - far right. Settings and Logout now live inside
                this dropdown instead of as standalone bar items. */}
            <div className="ml-3 relative" ref={profileMenuRef}>
              <button
                type="button"
                onClick={() => setProfileMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-white/80 hover:bg-white shadow-sm border border-gray-200 transition-all duration-200"
              >
                <div className="relative">
                  <img
                    className={`w-10 h-10 rounded-full object-cover border-2 ${debouncedConnectionStatus === "connected"
                      ? "border-green-200"
                      : "border-red-200"
                      }`}
                    src={
                      profileImage !== "null"
                        ? `data:image/png;base64,${profileImage}`
                        : "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                    }
                    alt="profile"
                  />
                  <span
                    className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${debouncedConnectionStatus === "connected"
                      ? "bg-green-500"
                      : debouncedConnectionStatus === "reconnecting"
                        ? "bg-yellow-500 animate-pulse"
                        : "bg-red-500"
                      }`}
                    title={
                      debouncedConnectionStatus === "connected"
                        ? "Online"
                        : debouncedConnectionStatus === "reconnecting"
                          ? "Reconnecting..."
                          : "Offline"
                    }
                  />
                </div>
                <FaChevronDown
                  className={`text-xs text-gray-500 transition-transform duration-200 ${profileMenuOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 max-h-[calc(100dvh-90px)] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      toggleProfileModal();
                    }}
                    title="Open Profile"
                    className="flex items-center gap-3 w-full px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 text-left"
                  >
                    <img
                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                      src={
                        profileImage !== "null"
                          ? `data:image/png;base64,${profileImage}`
                          : "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                      }
                      alt="profile"
                    />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        Welcome
                      </div>
                      <div className="text-base font-bold text-gray-800 truncate">
                        {fullName}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {data?.Email}
                      </div>
                    </div>
                  </button>

                  {shouldShowReconnect && (
                    <button
                      type="button"
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-[15px] font-medium text-blue-600 hover:bg-blue-50 transition-colors duration-200 border-b border-gray-100"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        handleReconnect();
                      }}
                    >
                      <FaWifi />
                      Reconnect
                    </button>
                  )}

                  {showSettingsButton && (
                    <button
                      type="button"
                      className="flex items-center justify-between w-full px-4 py-2.5 text-[15px] font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        setting();
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <FaGear className="text-gray-500" />
                        Settings
                      </span>
                      <FaChevronRight className="text-xs text-gray-400" />
                    </button>
                  )}

                  <ThemePicker theme={theme} onChange={changeTheme} />

                  <div className="border-t border-gray-100" />

                  <button
                    type="button"
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-[15px] font-medium text-red-600 hover:bg-red-50 transition-colors duration-200"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <FaRightFromBracket />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Profile Modal */}
      {isProfileModalOpen && (
        <CustomModal
          isLoading={false}
          title="Profile"
          size="md"
          ModalBody={
            <UserProfileBody
              onClose={toggleProfileModal}
              profileImage={profileImage}
            />
          }
          onClose={toggleProfileModal}
        />
      )}
    </div>
  );
};

// Reusable Components
const NavButton = ({ active, label, icon, onClick }) => (
  <button
    type="button"
    className={`pc-nav-button flex items-center px-4 py-2 text-base font-semibold rounded-lg transition-all duration-200 ${active
      ? "bg-white shadow-md text-blue-700 border border-blue-200"
      : "bg-white/70 hover:bg-white hover:shadow-md text-gray-700 border border-gray-200"
      }`}
    onClick={onClick}
  >
    {icon && <span className="mr-2">{icon}</span>}
    <span>{label}</span>
  </button>
);

const DropdownItem = ({ active, label, icon, onClick }) => (
  <button
    className={`flex items-center w-full px-4 py-3 text-[15px] font-medium transition-colors duration-200 ${active
      ? "bg-blue-50 text-blue-700 border-r-2 border-blue-500"
      : "text-gray-700 hover:bg-gray-50"
      }`}
    onClick={onClick}
  >
    {icon && <span className="mr-3">{icon}</span>}
    <span>{label}</span>
  </button>
);

const MobileNavItem = ({ active, label, icon, handler }) => (
  <button
    className={`flex items-center w-full px-4 py-3 text-[15px] font-medium transition-colors duration-200 ${active
      ? "bg-blue-50 text-blue-700 border-r-2 border-blue-500"
      : "text-gray-700 hover:bg-gray-50"
      }`}
    onClick={handler}
  >
    {icon && <span className="mr-3">{icon}</span>}
    <span>{label}</span>
  </button>
);

export default Index;
